/**
 * Hook pour gérer les opérations Twitch CLI
 * @module admin/hooks/useTwitchCli
 */

import { useState, useEffect, useCallback } from 'react';
import { getCliStatus, getAvailableEvents, triggerEvent } from '../services/twitchCliService';
import { eventFieldsConfig } from '../constants/eventFieldsConfig';
import { getDefaultEventOptions, TWITCH_CLI_HISTORY_LIMIT } from '../constants/defaultValues';

/**
 * Hook de gestion Twitch CLI
 * @returns {Object} État et fonctions pour Twitch CLI
 */
export const useTwitchCli = () => {
  const [cliStatus, setCliStatus] = useState(null);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [eventOptions, setEventOptions] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);

  /**
   * Récupère le statut du CLI
   */
  const fetchCliStatus = useCallback(async () => {
    try {
      const data = await getCliStatus();
      setCliStatus(data);
    } catch (err) {
      setCliStatus({ installed: false, error: err.message });
    }
  }, []);

  /**
   * Récupère les événements disponibles
   */
  const fetchAvailableEvents = useCallback(async () => {
    try {
      const data = await getAvailableEvents();
      if (data.success) {
        setAvailableEvents(data.events);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des événements:', err);
    }
  }, []);

  /**
   * Gère le changement d'événement sélectionné
   * @param {string} eventType - Type d'événement
   */
  const handleEventChange = useCallback((eventType) => {
    setSelectedEvent(eventType);
    setEventOptions({});

    const fields = eventFieldsConfig[eventType];
    if (fields) {
      const defaultOptions = getDefaultEventOptions(eventType, fields);
      setEventOptions(defaultOptions);
    }

    setResult(null);
    setError(null);
  }, []);

  /**
   * Gère le changement d'une option d'événement
   * @param {string} fieldName - Nom du champ
   * @param {*} value - Nouvelle valeur
   */
  const handleOptionChange = useCallback((fieldName, value) => {
    setEventOptions((prev) => {
      const newOptions = {
        ...prev,
        [fieldName]: value,
      };

      // Mettre à jour automatiquement le reward_title selon le reward_type
      if (
        fieldName === 'reward_type' &&
        selectedEvent === 'channel.channel_points_custom_reward_redemption.add'
      ) {
        const rewardTitleMap = {
          normal: 'Ajouter une musique',
          random: 'Musique aléatoire',
          wtf: 'Musique WTF',
        };
        newOptions.reward_title = rewardTitleMap[value] || 'Ajouter une musique';
      }

      return newOptions;
    });
  }, [selectedEvent]);

  /**
   * Déclenche un événement
   */
  const handleTriggerEvent = useCallback(async () => {
    if (!selectedEvent) {
      setError('Veuillez sélectionner un événement');
      return;
    }

    setIsLoading(true);
    setResult(null);
    setError(null);

    try {
      const data = await triggerEvent(selectedEvent, eventOptions);

      setResult(data);

      const historyEntry = {
        id: Date.now(),
        eventType: selectedEvent,
        options: { ...eventOptions },
        result: data,
        timestamp: new Date().toISOString(),
      };
      setHistory((prev) => [historyEntry, ...prev].slice(0, TWITCH_CLI_HISTORY_LIMIT));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedEvent, eventOptions]);

  /**
   * Rejoue un événement depuis l'historique
   * @param {Object} entry - Entrée de l'historique
   */
  const replayEvent = useCallback((entry) => {
    setSelectedEvent(entry.eventType);
    setEventOptions(entry.options);
  }, []);

  /**
   * Supprime une entrée de l'historique
   * @param {number} id - ID de l'entrée
   */
  const deleteHistoryEntry = useCallback((id) => {
    setHistory((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  /**
   * Efface tout l'historique
   */
  const clearHistory = useCallback(() => {
    setHistory([]);
    localStorage.removeItem('twitchCliHistory');
  }, []);

  // Charger l'historique depuis localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('twitchCliHistory');
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory));
      } catch (err) {
        console.error('Erreur lors du chargement de l\'historique:', err);
      }
    }
  }, []);

  // Sauvegarder l'historique dans localStorage
  useEffect(() => {
    if (history.length > 0) {
      localStorage.setItem(
        'twitchCliHistory',
        JSON.stringify(history.slice(0, TWITCH_CLI_HISTORY_LIMIT))
      );
    }
  }, [history]);

  const isCliReady = cliStatus?.installed;

  return {
    cliStatus,
    availableEvents,
    selectedEvent,
    eventOptions,
    isLoading,
    result,
    error,
    history,
    isCliReady,
    fetchCliStatus,
    fetchAvailableEvents,
    handleEventChange,
    handleOptionChange,
    handleTriggerEvent,
    replayEvent,
    deleteHistoryEntry,
    clearHistory,
  };
};
